import { WSPageToolbarAction } from "@wingspanhq/fe-component-library";
import {
  SharedFileRequest,
  SharedFileRequestStatus
} from "@wingspanhq/wingspan-api-sdk";

import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import { useDownloadVaultFile } from "../../../../hooks/useDownloadVaultFile";
import { selectorIsRequirementReadyToDownloadFile } from "../../../../selectors/selectorIsRequirementReadyToDownloadFile";
import { selectorSharedFileExtractedFields } from "../../../../selectors/selectorSharedFileExtractedFields";
import {
  CellsConfig,
  ContextItem,
  RequirementContextType,
  RequirementTableCallback,
  RequirementTableItemData
} from "../../../../types";
import { getRequirementEngagementsTableCellProps } from "../../../../utils";
import { SharedFileDetailsWidgetsContainer } from "../../components/SharedFileDetailsWidgetsContainer";
import { getSharedFilePayerRequirementStatus } from "../helpers/getSharedFilePayerRequirementStatus";
import { getSharedFileProgressSteps } from "../helpers/getSharedFileProgressSteps";
import { getSharedFileRequirementTitle } from "../helpers/getSharedFileRequirementTitle";
import { mapContextTypeExtractedFieldsToDataView } from "../helpers/mapContextTypeExtractedFieldsToDataView";
import { renderSharedFileName } from "../helpers/renderSharedFileName";
import { SHARED_FILE_REQUIREMENT_EVENT } from "../utils";

import { payeeSharedFileRenderCardDescription } from "./payeeSharedFileRenderCardDescription";

export const usePayeeSharedFileRequirementConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  RequirementTableItemData,
  ContextItem,
  SharedFileRequest | undefined
> => {
  const downloadVaultFile = useDownloadVaultFile();

  const handleRequirementDownload = async (
    requirement: RequirementTableItemData,
    fileId: string
  ) => {
    if (await downloadVaultFile(fileId)) {
      callback(SHARED_FILE_REQUIREMENT_EVENT.DownloadAsPayee, requirement);
    }
  };

  const getDownloadFileAction = (
    requirement: RequirementTableItemData,
    context: ContextItem,
    sharedFileRequest: SharedFileRequest | undefined
  ) => {
    const isDocumentReadyToDownload = selectorIsRequirementReadyToDownloadFile(
      requirement,
      sharedFileRequest
    );

    if (isDocumentReadyToDownload) {
      return async () => {
        await handleRequirementDownload(
          requirement,
          sharedFileRequest?.fileId!
        );
      };
    }

    return undefined;
  };

  return {
    isActionRequired: () => {
      return false;
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      sharedFileRequest
    ) => {
      return sharedFileRequest?.status
        ? getSharedFileProgressSteps(
            sharedFileRequest.status,
            sharedFileRequest.contextType
          )
        : undefined;
    },
    requirementDetailsWidgets: (requirement, context, sharedFileRequest) => {
      return sharedFileRequest?.id ? (
        <SharedFileDetailsWidgetsContainer
          contextType={RequirementContextType.Payee}
          requirement={requirement}
          sharedFileRequestId={sharedFileRequest.id}
          onSuccess={callback}
        />
      ) : null;
    },
    requirementName: getSharedFileRequirementTitle,
    requirementDetails: (requirement, context, sharedFileRequest) => {
      const downloadAction = getDownloadFileAction(
        requirement,
        context,
        sharedFileRequest
      );
      const extractedFields =
        selectorSharedFileExtractedFields(sharedFileRequest);

      return {
        header: {
          label: {
            text: renderSharedFileName(
              requirement,
              context,
              sharedFileRequest
            ) as string
          }
        },
        data: extractedFields
          ? mapContextTypeExtractedFieldsToDataView(
              extractedFields,
              sharedFileRequest?.contextType
            )
          : [],
        children: payeeSharedFileRenderCardDescription(
          requirement,
          sharedFileRequest
        ),
        actions: downloadAction
          ? {
              alignment: "fill",
              buttons: [
                {
                  label: "Download file",
                  onAsyncClick: downloadAction,
                  kind: "Secondary"
                }
              ]
            }
          : undefined
      };
    },
    requirementStatusAlert: (requirement, contextItem, sharedFileRequest) => {
      if (
        sharedFileRequest?.status === SharedFileRequestStatus.PENDING_UPLOAD ||
        sharedFileRequest?.status === SharedFileRequestStatus.PENDING_SUBMISSION
      ) {
        return {
          theme: "info",
          title: "Awaiting contractor",
          children: "Awaiting contractor to complete the requirement.",
          icon: "time"
        };
      }

      return undefined;
    },
    toolbarActions: (requirement, context, sharedFileRequest) => {
      const actions: WSPageToolbarAction[] = [];
      const downloadAction = getDownloadFileAction(
        requirement,
        context,
        sharedFileRequest
      );

      if (downloadAction) {
        actions.push({
          onAsyncClick: downloadAction,
          label: "Download file",
          buttonKind: "Secondary",
          icon: "download",
          buttonIcon: null
        });
      }

      return actions;
    },
    nameCell: (requirement, context, sharedFileRequest) => {
      return {
        text: getSharedFileRequirementTitle(
          requirement,
          context,
          sharedFileRequest
        )
      };
    },
    typeCell: (requirement, _, sharedFileRequest) =>
      getRequirementDefinitionTableCellProps({
        type: requirement.type,
        contextType: sharedFileRequest?.contextType
      }),
    statusCell: (requirement, context, sharedFileRequest) => {
      return getSharedFilePayerRequirementStatus(
        requirement,
        context,
        sharedFileRequest
      );
    },
    engagementsCell: getRequirementEngagementsTableCellProps
  };
};
